import { RetryErrorCard, Show } from '@dvb/ui';
import { cn } from '@heroui/react';
import { animate, motion } from 'framer-motion';
import {
  type HTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends HTMLAttributes<HTMLDivElement> {
  image?: string;
  size: number;
}

enum Status {
  ERROR = 'error',
  IDLE = 'idle',
  LOADED = 'loaded',
}

/**
 * References:
 * @see https://codepen.io/amit_sheen/pen/ZEJdamr
 * @see https://codepen.io/ahmad_hania/pen/rRPKbZ
 */
export function Nft3DCard({ className, image = undefined, size }: Props) {
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement>(null);
  const [status, setStatus] = useState<Status>(Status.IDLE);

  const handleReload = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (imgRef && image) {
        const img = new Image();
        img.src = image;
        img.onload = () => {
          setStatus(Status.LOADED);
          resolve(true);
        };
      }
      reject(new Error());
    });
  }, [image]);

  useEffect(() => {
    if (imgRef.current && status === 'loaded') {
      const rubberBandKeyframes = [
        size * 0.33,
        size * 0.66,
        size,
        size * 1.15,
        size * 0.95,
        size * 1.05,
        size,
      ];
      animate(
        imgRef.current,
        {
          maxHeight: rubberBandKeyframes,
          maxWidth: rubberBandKeyframes,
          opacity: 1,
        },
        { duration: 1 }
      );
    }
  }, [status, size]);

  return (
    <Show
      fallback={
        <div
          className={cn('mx-auto', className)}
          style={{ height: size, width: size }}
        >
          <RetryErrorCard handleReload={handleReload} />
        </div>
      }
      when={status !== 'error'}
    >
      <div
        className={cn('relative flex items-center justify-center', className)}
        style={{ height: size }}
      >
        <motion.div
          animate={{
            rotateX: [15, 0, -15, 0, 15],
            rotateY: [15, -15, 15, -15, 15],
          }}
          className="flex items-center justify-center"
          initial={{ rotateX: 15, rotateY: 15 }}
          style={{
            transformStyle: 'preserve-3d',
          }}
          transition={{
            duration: 20,
            ease: 'easeInOut',
            repeat: Infinity,
            repeatType: 'loop',
          }}
        >
          {/* Gradient border animate effect */}
          <Show when={status === Status.LOADED}>
            <motion.div
              animate={{ opacity: 1 }}
              className="animate-bg-gradient absolute -z-10"
              initial={{ opacity: 0 }}
              style={{
                height: size + 5,
                maskImage: image ? `url(${image})` : undefined,
                maskPosition: 'center',
                maskRepeat: 'no-repeat',
                maskSize: 'contain',
                width: size + 5,
              }}
              transition={{ delay: 1, duration: 0.3 }}
            />
          </Show>

          {/* Main content */}
          <motion.img
            alt={t('logo')}
            className="text-[0px]"
            initial={{ maxHeight: 0, maxWidth: 0, opacity: 0 }}
            onError={() => {
              setStatus(Status.ERROR);
            }}
            onLoad={() => {
              setStatus(Status.LOADED);
            }}
            ref={imgRef}
            src={image}
          />

          {/* Shine animate effect */}
          <Show when={status === Status.LOADED}>
            <motion.div
              animate={{ opacity: 1 }}
              className="absolute"
              initial={{ opacity: 0 }}
              style={{
                height: size,
                maskImage: image ? `url(${image})` : undefined,
                maskPosition: 'center',
                maskRepeat: 'no-repeat',
                maskSize: 'contain',
                width: size,
              }}
              transition={{ delay: 1, duration: 0.3 }}
            >
              <div className="animate-shine" />
            </motion.div>
          </Show>

          {/* Simulate the depth (thickness) of the image */}
          <div
            className="absolute inset-0 -z-10"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {Array.from(Array(10).keys()).map((i) => (
              <motion.div
                className="absolute inset-0 bg-contain bg-center bg-no-repeat"
                key={i}
                style={{
                  backgroundImage: image ? `url(${image})` : undefined,
                  transform: `translateZ(${i * -4}px)`,
                }}
              />
            ))}
          </div>
        </motion.div>

        {/* Use gradient light to simulate placeholder when image is not loaded */}
        <motion.div
          animate={status === Status.LOADED && { opacity: 0 }}
          className="animate-bg-gradient absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 blur-xl"
          initial={{ opacity: 1 }}
          style={{
            height: size / 1.5,
            width: size / 1.5,
          }}
          transition={{ duration: 1 }}
        />
      </div>
    </Show>
  );
}
